import SEOHead from './SEOHead';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, MapPin, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const MyEvents = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/events/my/registrations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRegs(res.data.registrations || []);
      } catch (e) {
        toast.error('Failed to load your events');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBA';

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <SEOHead title="My Events" description="Your registered events on Nerd Nation." path="/my-events" noIndex={true} />
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-orange-500" /> My Events
        </h1>
        <p className="text-sm text-gray-600 mb-6">Your registered events</p>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : regs.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No event registrations yet</p>
            <button onClick={() => navigate('/events')} className="mt-3 text-sm text-orange-600 font-medium hover:underline">Browse events →</button>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {regs.map((r) => (
              <Card key={r.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => r.event && navigate(`/events/${r.event.slug || r.event_id}`)}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 flex items-center justify-center flex-shrink-0">
                      {r.event?.banner_url ? (
                        <img src={r.event.banner_url} alt="" className="w-full h-full object-cover rounded-lg" loading="lazy" decoding="async"/>
                      ) : <Sparkles className="h-6 w-6 text-orange-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{r.event_title || r.event?.title || 'Event'}</div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                        {r.event?.event_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(r.event.event_date)}</span>}
                        {(r.event?.venue || r.event?.online_link) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.event.venue || 'Online'}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="h-3 w-3 mr-1" />Registered</Badge>
                      {r.payment_status === 'paid' && r.amount_paid ? (
                        <span className="text-xs text-gray-500">Paid ₹{r.amount_paid.toLocaleString('en-IN')}</span>
                      ) : (
                        <span className="text-xs text-green-600 font-medium">Free</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyEvents;
